import { Box, Button, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { colors, theme } from '../theme'
import image from './Functionalities/images/featureTitleShape.png'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { CustomersClient } from '../../interactors/clients/CustomerClient'
import {
  CustomerDto,
  CustomerPreferencesDto,
  OrganizationTypeDto,
  OrganizationTypeDtoType,
} from '../../interactors/gen/backendClient'
import { useFetchOrRefreshMembersOnMount } from '../../store/useFetchOrRefreshMembersOnMount'
import { EditTheAssociationPrompt } from './MyAssociation/EditTheAssociationPrompt'
import { CircularProgressPanel } from '../common/CircularProgressPanel'
import axios, { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'
import { useMutation } from 'react-query'
import { readableAxiosError } from '../../utils/axios'
import { useAccountsStore } from '../../store/AccountsStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../store/useFetchOrRefreshAdoptersOnMount'
import { useAdoptersStore } from '../../store/AdoptersStore'
import { backendClient } from '../../interactors/clients/client'
import { LoadingButton } from '@mui/lab'
import { StripeConnectButton } from '../Adopters/StripeConnectButton'
import { useMemberAccessPolicy } from '../../utils/hooks/useMemberAccessPolicy'

export const CompletionScreen: React.FC = () => {
  const globalSnackBarStore = useGlobalSnackbarStore()
  const accountsStore = useAccountsStore()
  const { policy: memberAccessPolicy } = useMemberAccessPolicy(
    accountsStore.connectedAccount?.customerId,
    Boolean(accountsStore.connectedAccount)
  )
  const adoptersStore = useAdoptersStore()
  const navigate = useNavigate()
  useFetchOrRefreshMembersOnMount()
  useFetchOrRefreshAdoptersOnMount()

  const [stripeDone, setStripeDone] = React.useState(false)

  const finalizedAttemptsCount = adoptersStore.adopters.reduce((count, adopter) => {
    if (adopter.adoptionAttempts && adopter.adoptionAttempts.length > 0) {
      return (
        count +
        adopter.adoptionAttempts.filter((attempt) =>
          attempt.events.some(
            (event) => event.type === 'payment-reception' && event.comment && event.comment.includes('reçu par Petso')
          )
        ).length
      )
    }
    return count
  }, 0)

  const [association, setAssociation] = React.useState<CustomerDto | null>(null)

  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const response = await backendClient.get('/customers/stripe-connect-status')
        const { connected } =
          response.data === true || response.data === 'valid' ? { connected: true } : { connected: false }
        setStripeDone(connected)
      } catch (error) {
        console.error('Error fetching Stripe status:', error)
        setStripeDone(false)
      }
    }
    fetchStripeStatus()
  }, [])

  useEffect(() => {
    const loadAssociation = async () => {
      const selectedAssociation = await CustomersClient.getCurrentCustomer()
      setAssociation(selectedAssociation || null)
    }

    loadAssociation()
  }, [])

  //Mutation to edit the customer
  const editAssociationMutation = useMutation(
    async (data: CustomerDto) => {
      const response = await CustomersClient.editCustomer(data)
      return response
    },
    {
      onSuccess: (association) => {
        globalSnackBarStore.triggerSuccessMessage(`Le profil de ${association.name} a été modifié.`)
        const fetchCustomer = async () => {
          const customer = await CustomersClient.getCurrentCustomer()
          setAssociation(customer)
          accountsStore.changeConnectedCustomer(customer)
        }

        fetchCustomer()
      },
      onError: (error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === StatusCodes.CONFLICT) {
            globalSnackBarStore.triggerErrorMessage('Un utilisateur existe déjà avec cette addresse mail.')
            return
          }
        }

        globalSnackBarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
      },
    }
  )

  const getFreeTrial = async () => {
    if (memberAccessPolicy?.trial.isEligibleForNewPolicy) {
      globalSnackBarStore.triggerSuccessMessage(`Votre période d'essai de 3 mois avec licences illimitées est active.`)
      navigate('/association')
      return
    }

    try {
      const response = await backendClient.post('/customers/free-trial')
      if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
        globalSnackBarStore.triggerSuccessMessage(`Votre période d'essai gratuite a été activée.`)
        navigate('/association')
      } else {
        globalSnackBarStore.triggerErrorMessage(`Une erreur est survenue lors de l'activation de la période d'essai.`)
      }
    } catch (error) {
      console.error('Error activating free trial:', error)
      globalSnackBarStore.triggerErrorMessage(`Une erreur est survenue lors de l'activation de la période d'essai.`)
    }
  }

  const onSubmit = async (
    data: Partial<CustomerDto> & Partial<CustomerPreferencesDto> & Partial<OrganizationTypeDto>
  ) => {
    if (!association) {
      console.error('Association is not loaded')
      return
    }
    const organizationType = {
      type: data.type || association.organizationType?.type,
      otherField:
        data.type === OrganizationTypeDtoType.OtherOrganizations
          ? data?.otherField || association.organizationType?.otherField
          : undefined,
    }
    const preferences = {
      species: data.species || association.preferences.species,
    }
    console.log('On submit', data)

    const { species, ...restData } = data

    const newData = {
      ...association,
      ...restData,
      organizationType: organizationType,
      preferences: preferences,
      phoneNumber: ['+33', ''].includes(data.phoneNumber?.trim() || '') ? undefined : data.phoneNumber,
    }
    console.log('newData', newData)
    editAssociationMutation.mutate(newData)
  }

  const assoHasCertificate = association?.pdfTemplates?.some(
    (template) => template.type === 'knowledge-certificate-model'
  )
  const assoHasContract = association?.pdfTemplates?.some((template) => template.type === 'adoption-contract-model')

  const isCustomerFilledAppropriately = (customer: CustomerDto | null): boolean => {
    if (!customer) return false
    return !!(
      customer.name &&
      customer.email &&
      customer.phoneNumber &&
      customer.address &&
      customer.postalCode &&
      customer.city &&
      customer.organizationType?.type &&
      customer.preferences?.species &&
      customer.preferences.species.length > 0 &&
      (customer.SIRETNumber || customer.RNANumber)
    )
  }

  if (!association) {
    return <CircularProgressPanel />
  }

  const isSubscribed = association?.subscription?.status === 'active'
  const isPremium = association?.subscription?.plan === 'premium' && isSubscribed
  const isAdmin = accountsStore.isAdmin()

  if (isPremium || !isAdmin) {
    return <Navigate to="/association" />
  }

  return (
    <Box sx={{ padding: { xs: 1, md: 2 }, backgroundColor: 'grey.100', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: '1200px', marginX: 'auto', marginBottom: '2rem', mt: 4 }}>
        <Box
          sx={{
            backgroundColor: colors.blueSky,
            color: 'white',
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            width: 'fit-content',
            p: { xs: 0.5, sm: 1 },
            py: 0.3,
            pr: { xs: 2, sm: 3 },
            pl: { xs: 2, sm: 3 },
            borderRadius: '8px',
            transform: 'rotate(-1deg)',
            mb: { xs: 2, sm: 3 },
          }}
        >
          🎁 Finalisez la configuration de votre compte et profitez de l&apos;offre utile !
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 14, sm: 16 },
              color: 'black',
            }}
          >
            Gagnez du temps et restez en conformité en exploitant tout le potentiel de Petso
          </Typography>
          <Typography sx={{ color: colors.cello, fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
            Licences illimitées gratuites (au lieu de 490 €/an) + toutes les fonctionnalités avancées.
          </Typography>
        </Box>

        <TitleWithNumber
          number={1}
          title="Compléter le profil de votre association"
          subtitle="Coordonnées, Siret/RNA, éléments administratifs"
          button={
            <EditTheAssociationPrompt association={association} onSubmit={(data) => onSubmit(data)} variantButton />
          }
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 14, sm: 16 },
              color: 'black',
            }}
          >
            Configurer votre procédure d&apos;adoption Petso
          </Typography>
          <Typography sx={{ color: colors.cello, fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
            Centralisez votre gestion des adoption : création, documents auto-générés, signature électronique, paiement
            en ligne sécurisé – tout est tracé et archivé dans la fiche de l&apos;animal.
          </Typography>
        </Box>

        <TitleWithNumber
          number={2}
          title="Les modèles de documents : "
          subtitle="Créez un modèle à votre image à partir d'un PDF, définissez les zones de remplissage automatique (variables) et les zones à faire remplir par l'adoptant (champs)"
          link={
            <a
              href="https://www.youtube.com/watch?v=1oO1jA-VLNo"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              - Voir le tuto
            </a>
          }
        />

        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ml: { xs: 8, sm: 12 }, mb: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 13, sm: 15 },
                color: 'black',
              }}
            >
              a. Configurer le modèle de certificat (Facultatif)
            </Typography>
            <Typography sx={{ color: colors.blueSky, fontSize: { xs: '11px', sm: '13px' }, fontWeight: 500 }}>
              Facultatif : Petso propose un modèle générique prêt à l&apos;emploi si cela vous convient.
            </Typography>
          </Box>
          <Button
            variant={assoHasCertificate ? 'contained' : 'outlined'}
            color={assoHasCertificate ? 'success' : 'primary'}
            sx={{ textTransform: 'none', fontWeight: 700 }}
            onClick={() => {
              navigate('/association?tab=templates&new=true')
            }}
          >
            {assoHasCertificate ? `Parfait !` : `À compléter`}
          </Button>
        </Box>

        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ml: { xs: 8, sm: 12 }, mb: 2 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 13, sm: 15 },
                color: 'black',
              }}
            >
              b. Configurer le modèle de contrat (Facultatif)
            </Typography>
            <Typography sx={{ color: colors.blueSky, fontSize: { xs: '11px', sm: '13px' }, fontWeight: 500 }}>
              Facultatif : Petso propose un modèle générique prêt à l&apos;emploi si cela vous convient.
            </Typography>
          </Box>
          <Button
            variant={assoHasContract ? 'contained' : 'outlined'}
            color={assoHasContract ? 'success' : 'primary'}
            sx={{ textTransform: 'none', fontWeight: 700 }}
            onClick={() => {
              navigate('/association?tab=templates&new=true')
            }}
          >
            {assoHasContract ? `Parfait !` : `À compléter`}
          </Button>
        </Box>

        <TitleWithNumber
          number={3}
          title="Configurer le paiement en ligne"
          subtitle="Pour garantir fiabilité et sécurité, Stripe est notre partenaire. Le paiement est sans frais."
          button={
            stripeDone ? (
              <LoadingButton variant="contained" color="success" sx={{ textTransform: 'none', fontWeight: 700 }}>
                Parfait !
              </LoadingButton>
            ) : (
              <StripeConnectButton
                variant="contained"
                color="primary"
                fullWidth={false}
                sx={{ textTransform: 'none', fontWeight: 700 }}
                content="À compléter"
              />
            )
          }
        />

        <TitleWithNumber
          number={4}
          title="Lancer 3 procédures avec Petso"
          subtitle="De la création d'un adoptant à sa signature des documents et son paiement en ligne via Petso"
          link={
            <Link
              to={'/suivi-adoptant/ajouter-adoptant'}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              - Démarrer une procédure
            </Link>
          }
          button={
            <Button
              variant="contained"
              color={finalizedAttemptsCount > 2 ? 'success' : 'primary'}
              sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={() => navigate('/suivi-adoptant/ajouter-adoptant')}
            >
              {finalizedAttemptsCount > 2 ? `Parfait !` : `${finalizedAttemptsCount} / 3`}
            </Button>
          }
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
            justifyContent: 'space-between',
            alignItems: 'center',
            my: 1.5,
            mt: 3,
          }}
        >
          <LoadingButton
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              height: { xs: '36px', sm: '42px' },
              width: 'fit-content',
              borderRadius: { xs: '36px', sm: '42px' },
              fontSize: { xs: '14px', sm: '16px' },
              px: { xs: 2, sm: 3 },
            }}
            onClick={() => {
              getFreeTrial()
            }}
            disabled={!stripeDone || finalizedAttemptsCount <= 2 || !isCustomerFilledAppropriately(association)}
          >
            {!stripeDone || finalizedAttemptsCount <= 2 || !isCustomerFilledAppropriately(association) ? '🔒 ' : ''}
            {memberAccessPolicy?.trial.isEligibleForNewPolicy
              ? 'Continuer vers mon association'
              : "Bénéficier de l'offre utile"}
          </LoadingButton>
          {stripeDone && finalizedAttemptsCount > 2 && isCustomerFilledAppropriately(association) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 14, sm: 16 },
                  color: colors.seaGreen,
                }}
              >
                Félicitations, votre compte est prêt !
              </Typography>
              <Typography sx={{ color: colors.cello, fontSize: { xs: '12px', sm: '14px' }, fontWeight: 500 }}>
                Invitez dès maintenant tous vos membres pour activer votre équipe et exploiter tout le potentiel de
                Petso.
              </Typography>
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 11, sm: 12 },
            color: 'black',
            mt: 2,
            mb: 1,
          }}
        >
          Besoin d&apos;aide ? contactez-nous à{' '}
          <a href="mailto:contact@petso.fr" target="_blank" rel="noreferrer" style={{ color: colors.cello }}>
            contact@petso.fr
          </a>
        </Typography>
      </Box>
    </Box>
  )
}

interface TitleWithNumberProps {
  number: number
  title: string
  subtitle: string
  link?: React.ReactNode
  button?: React.ReactNode
}

export const TitleWithNumber: React.FC<TitleWithNumberProps> = ({ number, title, subtitle, link, button }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ml: { xs: 3, sm: 4 }, mb: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: { xs: '12px', sm: '16px' } }}>
        <Box sx={{ position: 'relative' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 13, sm: 15 },
              color: 'black',
              position: 'relative',
              zIndex: 1,
              ml: { xs: '12px', sm: '15px' },
              whiteSpace: 'nowrap',
            }}
          >
            {number}.
          </Typography>
          <img
            src={image}
            alt="Abstract form to add nice design"
            width={28}
            height={28}
            style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 13, sm: 15 },
              color: 'black',
            }}
          >
            {title} {link && link}
          </Typography>
          <Typography sx={{ color: colors.blueSky, fontSize: { xs: '11px', sm: '13px' }, fontWeight: 500 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      {button && button}
    </Box>
  )
}
